import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

// (2026-10) PDF especial de la entrada de proceso SIN desglose de tallas: sólo
// artículo, cantidad y precio (más la huara si tiene), con los totales de su
// nota de CxP. Lo usan Pagos y Saldos de CxP y el detalle de entradas de Cortes.

const txt = (v) => String(v ?? "").trim();
const dato = (v) => {
  const t = txt(v);
  return !t || ["NA", "NO"].includes(t.toUpperCase()) ? "" : t;
};
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n || 0));
const moneda = (n) =>
  "$" +
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    Number(n || 0)
  );
const fechaCorta = (f) => {
  const [a, m, d] = txt(f).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : txt(f);
};
const traer = async (url) =>
  (
    await axios.get(url, {
      headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
    })
  ).data;
const nombreArticulo = (a) =>
  [txt(a?.codigo), txt(a?.subDepartamentos?.[0]?.name)].filter((t) => t).join(" - ") ||
  txt(a?.descripcion) ||
  "Artículo";

// Desde la lista de entradas (Cortes): busca la nota de la entrada
export async function entradaSinTallasPorEntrada(idEntrada) {
  try {
    const nota = await traer(`${process.env.REACT_APP_URL_NOTAS_CXP}Entrada/${idEntrada}`);
    if (!nota?._id) return Swal.fire("Sin nota", "Esta entrada no tiene nota de CxP", "warning");
    await entradaSinTallasPorNota(nota._id);
  } catch (err) {
    console.error(err);
    Swal.fire("Error", "No se pudo generar el PDF de la entrada", "error");
  }
}

export async function entradaSinTallasPorNota(idNota) {
  try {
    const datos = await datosEntradaSinTallas(idNota);
    if (!datos) return Swal.fire("Sin entrada", "La nota no viene de una entrada de proceso", "warning");
    const doc = new jsPDF();
    dibujaEntradaSinTallas(doc, datos);
    piePaginas(doc, [{ desde: 1, ...pieEntrada(datos) }]);
    doc.save(`Entrada ${datos.entrada?.idProcesosEntrada ?? ""} - sin tallas.pdf`);
  } catch (err) {
    console.error(err);
    Swal.fire("Error", "No se pudo generar el PDF de la entrada", "error");
  }
}

// (2026-10) Nombre de la empresa para los PDF de CxP (la del pedido o el del sistema)
export const nombreEmpresaPDF = (empresa) => txt(empresa) || txt(process.env.REACT_APP_NOMBRE_INSTITUCION);

const pieEntrada = ({ entrada, empresa }) => ({
  izquierda: `${nombreEmpresaPDF(empresa).toUpperCase()} - Entrada de Proceso`,
  centro: `Entrada: ${entrada?.idProcesosEntrada ?? ""}`,
});

// (2026-10) Pie de página por secciones (un PDF puede juntar contrato y
// entradas): secciones = [{ desde: nº de hoja donde empieza, izquierda, centro }]
export function piePaginas(doc, secciones) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const n = doc.internal.getNumberOfPages();
  for (let i = 1; i <= n; i++) {
    const s = [...secciones].reverse().find((x) => x.desde <= i) || secciones[0] || {};
    doc.setPage(i);
    doc.setFont(undefined, "normal").setFontSize(8.5).setTextColor(60, 60, 60);
    doc.text(s.izquierda || "", 17, pageH - 12);
    doc.text(s.centro || "", pageW / 2, pageH - 12, { align: "center" });
    doc.text(`Página ${i} de ${n}`, pageW - 17, pageH - 12, { align: "right" });
  }
}

// Todo lo que lleva el PDF de una entrada, a partir de su nota de CxP
// (null si la nota no viene de una entrada de proceso)
export async function datosEntradaSinTallas(idNota) {
  const nota = await traer(`${process.env.REACT_APP_URL_NOTAS_CXP}/${idNota}`);
  const pe = nota?.procesosEntrada?.[0];
  if (!pe?._id) return null;
  const [entrada, renglones, abonos] = await Promise.all([
    traer(`${process.env.REACT_APP_URL_PROCESOS_ENTRADA}/${pe._id}`),
    traer(`${process.env.REACT_APP_URL_ARTICULOS_PROCESOS_ENTRADA}/proceso/${pe._id}`),
    // (2026-10) pagos y anticipos aplicados a la nota; sin ellos el PDF sale igual
    traer(`${process.env.REACT_APP_URL_CXP_PAGOS}AbonosNota/${idNota}`).catch((e) => {
      console.log("abonos de la nota:", e);
      return [];
    }),
  ]);
  let empresa = "";
  const idEmpresa = pe.pedidos?.[0]?.empresas?.[0];
  if (idEmpresa) {
    try {
      empresa = txt((await traer(`${process.env.REACT_APP_URL_EMPRESAS}/${idEmpresa._id || idEmpresa}`))?.name);
    } catch (e) {
      // sin empresa se usa el nombre del sistema
    }
  }
  return {
    nota,
    entrada,
    renglones: Array.isArray(renglones) ? renglones : [],
    abonos: Array.isArray(abonos) ? abonos : [],
    empresa,
  };
}

// Dibuja la entrada desde arriba de la hoja actual del doc (el pie lo pone piePaginas)
export function dibujaEntradaSinTallas(doc, { nota, entrada, renglones, abonos = [], empresa }) {
  const precio = Number(entrada?.precio || 0);
  const precioHuara = Number(nota?.precioHuara || 0);
  const importeHuara = Number(nota?.importeHuara || 0);

  // ---- un renglón por artículo (primeras + segundas, que se pagan igual)
  const porArticulo = new Map();
  renglones.forEach((r) => {
    const art = r.articulos?.[0] || r.articulosProcesosSalida?.[0]?.articulos?.[0] || entrada?.articulos?.[0];
    const k = txt(art?._id) || nombreArticulo(art);
    const g = porArticulo.get(k) || { nombre: nombreArticulo(art), cantidad: 0 };
    g.cantidad += Number(r.cantidadEntrada || 0) + Number(r.cantidadSegundas || 0);
    porArticulo.set(k, g);
  });
  let filas = [...porArticulo.values()].filter((g) => g.cantidad > 0);
  if (!filas.length) {
    filas = [{ nombre: nombreArticulo(entrada?.articulos?.[0]), cantidad: Number(nota?.total_cantidad || 0) }];
  }
  const piezas = filas.reduce((t, g) => t + g.cantidad, 0);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ML = 17;
  const anchoUtil = pageW - ML * 2;
  const azul = [30, 58, 138];
  const azulClaro = [37, 99, 235];
  const fondo = [238, 244, 255];
  const borde = [169, 180, 200];
  const rojo = [220, 38, 38];
  const nombreEmpresa = nombreEmpresaPDF(empresa);
  const taller = txt(nota?.proveedores?.[0]?.nombre_comercial || entrada?.proveedores?.[0]?.nombre_comercial);

  // ---- encabezado
  doc.setFont(undefined, "bold").setFontSize(17).setTextColor(...azul);
  doc.text(nombreEmpresa.toUpperCase(), ML, 20);
  doc.setFontSize(11.5).setTextColor(...azulClaro);
  doc.text("ENTRADA DE PROCESO", ML, 26.5);
  doc.setFontSize(10.5).setTextColor(...azul);
  doc.text("ENTRADA", pageW - ML, 19, { align: "right" });
  doc.setFontSize(16).setTextColor(...rojo);
  doc.text(String(entrada?.idProcesosEntrada ?? ""), pageW - ML, 26.5, { align: "right" });
  doc.setTextColor(0).setFont(undefined, "normal");

  const etiqueta = (content) => ({
    content,
    styles: { fillColor: fondo, textColor: azul, fontStyle: "bold" },
  });
  doc.autoTable({
    startY: 30,
    margin: { left: ML, right: ML },
    tableWidth: anchoUtil,
    theme: "grid",
    styles: { fontSize: 9.5, cellPadding: 2, lineColor: borde, lineWidth: 0.25, textColor: [30, 30, 30] },
    columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 58 }, 2: { cellWidth: 32 } },
    body: [
      [etiqueta("Taller:"), taller, etiqueta("Fecha:"), fechaCorta(entrada?.fecha || nota?.fecha)],
      [
        etiqueta("Proceso:"),
        txt(entrada?.catalogoProcesos?.[0]?.name),
        etiqueta("Contrato:"),
        txt(entrada?.procesosSalida?.[0]?.idProcesosSalida),
      ],
      [etiqueta("Corte #:"), txt(entrada?.cortes?.[0]?.idCorte), etiqueta("Nota CxP:"), txt(nota?.idNotasCxP)],
      [etiqueta("Factura:"), dato(nota?.factura), etiqueta("Piezas:"), num(piezas)],
    ],
  });

  // ---- artículo / cantidad / precio
  const body = filas.map((g) => [g.nombre, num(g.cantidad), moneda(precio), moneda(g.cantidad * precio)]);
  if (importeHuara > 0) {
    body.push(["Huara", num(Number(nota?.total_cantidad || piezas)), moneda(precioHuara), moneda(importeHuara)]);
  }
  doc.autoTable({
    startY: doc.autoTable.previous.finalY + 6,
    margin: { left: ML, right: ML, bottom: 22 },
    tableWidth: anchoUtil,
    theme: "grid",
    head: [["ARTÍCULO", "CANTIDAD", "PRECIO", "IMPORTE"]],
    body,
    styles: { fontSize: 10, cellPadding: 2.4, lineColor: borde, lineWidth: 0.25, textColor: [30, 30, 30] },
    headStyles: { fillColor: azul, textColor: 255, fontStyle: "bold", halign: "center" },
    columnStyles: {
      1: { cellWidth: 30, halign: "center" },
      2: { cellWidth: 32, halign: "right" },
      3: { cellWidth: 38, halign: "right" },
    },
  });

  // ---- (2026-10) abonos de la nota, con el saldo que quedaba a cada fecha
  // (como el PDF de notas de TextilesBasicos). El saldo parte del total.
  if (abonos.length) {
    let saldo = Number(nota?.total_costo || 0);
    const bodyAbonos = abonos.map((a) => {
      saldo -= Number(a.importe || 0);
      return [
        fechaCorta(a.fecha),
        [txt(a.concepto), dato(a.observaciones)].filter((t) => t).join(" · "),
        txt(a.banco),
        moneda(a.importe),
        moneda(Math.abs(saldo) < 0.005 ? 0 : saldo),
      ];
    });
    doc.autoTable({
      startY: doc.autoTable.previous.finalY + 5,
      margin: { left: ML, right: ML, bottom: 22 },
      tableWidth: anchoUtil,
      theme: "grid",
      head: [["FECHA", "ABONO", "BANCO", "IMPORTE", "SALDO A ESA FECHA"]],
      body: bodyAbonos,
      styles: { fontSize: 9, cellPadding: 2, lineColor: borde, lineWidth: 0.25, textColor: [30, 30, 30] },
      headStyles: { fillColor: fondo, textColor: azul, fontStyle: "bold", halign: "center" },
      columnStyles: {
        0: { cellWidth: 24, halign: "center" },
        2: { cellWidth: 30 },
        3: { cellWidth: 30, halign: "right" },
        4: { cellWidth: 44, halign: "right", fontStyle: "bold" },
      },
      rowPageBreak: "avoid",
    });
  }

  // ---- totales (los de la nota de CxP)
  const abonado = Math.max(0, Number(nota?.total_costo || 0) - Number(nota?.saldo || 0));
  const filasTot = [
    ["SUB TOTAL", moneda(nota?.subtotal)],
    // (2026-10) el IVA puede ser sólo sobre la parte que facturó el taller
    ...(Number(nota?.iva || 0) > 0 && nota?.facturaParcial === "Si"
      ? [["FACTURADO", moneda(nota.importeFacturado)]]
      : []),
    ...(Number(nota?.iva || 0) > 0
      ? [[`IVA ${Number(nota.impuestos || 0)}%`, moneda(nota.iva)]]
      : []),
    ["TOTAL", moneda(nota?.total_costo)],
    ...(abonado > 0.009 ? [["ABONADO", `-${moneda(abonado)}`]] : []),
    ["SALDO", moneda(nota?.saldo)],
  ];
  let y = doc.autoTable.previous.finalY + 4;
  if (y + filasTot.length * 9 + 45 > pageH - 22) {
    doc.addPage();
    y = 20;
  }
  doc.autoTable({
    startY: y,
    margin: { left: pageW - ML - 70, right: ML },
    tableWidth: 70,
    theme: "grid",
    body: filasTot.map(([l, v]) => [etiqueta(l), { content: v, styles: { halign: "right", fontStyle: l === "TOTAL" || l === "SALDO" ? "bold" : "normal" } }]),
    styles: { fontSize: 10, cellPadding: 2.3, lineColor: borde, lineWidth: 0.25 },
    columnStyles: { 0: { cellWidth: 30 } },
  });

  // ---- firmas
  const yFirma = doc.autoTable.previous.finalY + 32;
  const anchoFirma = 68;
  doc.setDrawColor(...azul).setLineWidth(0.35);
  doc.line(ML + 8, yFirma, ML + 8 + anchoFirma, yFirma);
  doc.line(pageW - ML - 8 - anchoFirma, yFirma, pageW - ML - 8, yFirma);
  doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azul);
  doc.text("Entregó (Taller)", ML + 8 + anchoFirma / 2, yFirma + 5.5, { align: "center" });
  doc.text("Recibió", pageW - ML - 8 - anchoFirma / 2, yFirma + 5.5, { align: "center" });
}
